/**
 * Auth helpers — thin wrappers around Firebase Auth that keep the UI
 * components free of Firebase imports.
 *
 * Now covers Google sign-in + email/password sign-up + sign-in + email
 * verification. users/{uid} doc creation + pending-Director-approval gate
 * land in the next PR (needs first real Firestore rules).
 */

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  getRedirectResult,
  linkWithCredential,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  updatePassword,
  type Auth,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { clearIndexedDbPersistence, terminate } from 'firebase/firestore';
import { auth, db } from './firebase';
import {
  normalizeOAuthSignInError,
  shouldFallBackToRedirect,
  shouldStartWithSameOriginRedirect,
} from './authSignInPolicy';
import { markReload } from './reloadGuard';
// STATIC on purpose, and from pushDeviceState.ts rather than push.ts (PR
// #1242 refute round, proved in Chromium). push.ts pulls firebase/messaging,
// so it is a lazy chunk now, and a browser caches a FAILED module fetch: a
// dynamic import of it here meant one failed push chunk refused every
// sign-out for the rest of that tab's life. pushDeviceState.ts holds the
// device token state and its removal without messaging, so sign-out never
// waits on a chunk. tests/harness/sign-out-push-chunk pins it.
import { cancelCurrentDeviceTokenRemoval, removeCurrentDeviceToken } from './pushDeviceState';
import { publicFunctionHeaders } from './functionSecurity';
import { clearSentryUser } from './sentry';
import { setPreviewRole } from './previewRole';
import { clearLastHomeShell } from './lastHomeShell';
import { resetFamilyStaffRolesCache } from './familyStaffRolesCache';
// Explicit .ts: a real runtime import from src/lib, which node's type
// stripping cannot resolve without the extension. See the note in
// lib/attendance.ts.
import { clearAttendanceConcernsCache } from './attendanceConcernsClient.ts';
import { clearComposerDraftsFor } from './composerDrafts';

/**
 * The AuthProvider listens for these events so it can replace the whole Hub
 * with a blocking sign-out screen before the first async cleanup step yields.
 * Without that gate Safari can render interactive signed-out controls after
 * fbSignOut while Firestore is still being terminated and wiped. Any auth
 * action in that window attaches listeners to a client that is already dead.
 */
export const SIGNING_OUT_EVENT = 'ldg:signing-out';
export const SIGNING_OUT_CANCELLED_EVENT = 'ldg:signing-out-cancelled';

const googleProvider = new GoogleAuthProvider();
// Always show account chooser even if there's a single session — important
// for shared family phones where mom and dad both use the studio app.
googleProvider.setCustomParameters({ prompt: 'select_account' });

function requireAuth() {
  if (!auth) throw new Error('Auth not initialized — check Firebase env vars.');
  return auth;
}


/**
 * Marker for "a full-page sign-in redirect was started from this browser".
 *
 * signInWithRedirect hands off to Google and comes back through the Firebase
 * helper on the Hub origin. The marker still matters if a browser drops the
 * handoff or storage is cleared: getRedirectResult can resolve to null without
 * throwing, which would otherwise leave the person at sign-in with no reason.
 *
 * Recording the attempt is what lets us tell "you never tried to sign in"
 * apart from "you tried and the browser ate it", and say so.
 */
const PENDING_REDIRECT_KEY = 'ldg:pendingSignInAt';
/** Older than this and it is stale, not a failure from this round trip. */
const PENDING_REDIRECT_MAX_AGE_MS = 10 * 60 * 1000;

function markRedirectStarted(): void {
  try {
    window.localStorage.setItem(PENDING_REDIRECT_KEY, String(Date.now()));
  } catch {
    // Private mode with storage disabled. The marker is a diagnostic, not a
    // dependency, so losing it must never block a sign-in.
  }
}

/**
 * Three answers, not two, for "is a Google return in flight on this load".
 *
 * 'none' means we positively READ the marker and it said no: an ordinary
 * boot with nothing to wait for. 'inflight' means a fresh mark, read
 * cleanly. 'unknown' is everything we cannot rule out: storage threw, the
 * mark was old enough to call stale but not old enough to rule out someone
 * who stopped at Google's account chooser and is only now coming back, or
 * our own mark was already consumed by an earlier load this round trip.
 * The caller (isRedirectReturnInFlight) treats 'unknown' the same as
 * 'inflight': the guard exists to protect one property (never publish the
 * previous account mid Google return) and a browser we cannot read is a
 * browser we cannot clear for that.
 */
type RedirectMarkState = 'none' | 'inflight' | 'unknown';

function takeRecentRedirectMark(): RedirectMarkState {
  let localState: RedirectMarkState;
  try {
    const raw = window.localStorage.getItem(PENDING_REDIRECT_KEY);
    if (!raw) {
      localState = 'none';
    } else {
      window.localStorage.removeItem(PENDING_REDIRECT_KEY);
      const startedAt = Number(raw);
      const fresh = Number.isFinite(startedAt) && Date.now() - startedAt < PENDING_REDIRECT_MAX_AGE_MS;
      // A finite but stale timestamp, or one that fails to parse, is not the
      // same claim as "no mark was ever written". Treat it as unknown rather
      // than none: it cannot prove a return is in flight, but it cannot rule
      // one out either.
      localState = fresh ? 'inflight' : 'unknown';
    }
  } catch {
    // Private mode, blocked site data. Cannot tell either way.
    localState = 'unknown';
  }
  if (localState !== 'none') return localState;
  // Our own mark says no, but takeRecentRedirectMark removes it on the
  // return's first load, so a reload mid-return has nothing of ours left to
  // read. Fall back to Firebase's own pending-redirect record, best effort:
  // present is only ever a real signal (the SDK writes it before leaving for
  // Google and nothing else does), so any unreadable answer stays 'unknown'
  // rather than being promoted to 'none'.
  return readFirebasePendingRedirectMark();
}

/**
 * Best-effort read of the installed Firebase Auth SDK's own redirect
 * marker, `firebase:pendingRedirect:<apiKey>:<appName>` in sessionStorage
 * (browserPopupRedirectResolver._redirectPersistence, confirmed by reading
 * node_modules/firebase/node_modules/@firebase/auth/dist/esm/index-*.js:
 * _persistenceKeyName + pendingRedirectKey). The SDK writes through
 * BrowserPersistenceClass._set, which JSON encodes, so the bytes are the
 * quoted string '"true"', not the bare word true. An earlier version of
 * this function compared the raw bytes against the bare word and could
 * never match what the SDK actually writes, so the fallback never fired in
 * production. This checks for PRESENCE, not an exact value: nothing but
 * the SDK ever writes this key, so any value at it is a real signal, and
 * presence also survives a future change to how the SDK encodes it.
 *
 * Said plainly about what this can and cannot catch: the SDK clears this
 * key itself as the very first await inside its own getRedirectResult
 * call, essentially immediately and well before any slower network step.
 * By the time a person can act on a slow boot (wait, then reload), that
 * call has usually already started on the earlier load, so this key is
 * usually already gone too by the reload. This fallback's real, provable
 * window is narrow: a mark blocked only in localStorage while
 * sessionStorage still works, and a reload that lands before this
 * module's own getRedirectResult call has begun. It can only ever ADD
 * coverage, never take any away. The fail-closed 'unknown' default above
 * this call, for a storage throw or a stale mark, is what actually
 * protects the wrong-person window on the harder timings this fallback
 * does not reach; this function is a narrow bonus, not the fix.
 */
function readFirebasePendingRedirectMark(): RedirectMarkState {
  if (!auth) return 'none';
  try {
    const key = `firebase:pendingRedirect:${auth.config.apiKey}:${auth.name}`;
    return window.sessionStorage.getItem(key) !== null ? 'unknown' : 'none';
  } catch {
    return 'unknown';
  }
}

/** Thrown when a redirect sign-in came back empty. Mapped to friendly copy. */
export const REDIRECT_SIGN_IN_LOST =
  'auth/redirect-result-lost';

function shouldUseRedirectSignIn(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  return shouldStartWithSameOriginRedirect({
    userAgent: navigator.userAgent,
    appHost: window.location.host,
    // Must match the authDomain the Firebase app was initialized with, which
    // follows the page on the Hub's own addresses (lib/firebase.ts). The env
    // value alone is the build-time default, not what this page uses.
    authDomain: auth?.config.authDomain ?? import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
  });
}

/**
 * Use the same-origin redirect on phones. Otherwise try a popup first and
 * redirect only if the popup could not run at all.
 *
 * Popup-first was added on 2026-07-27 because the old redirect helper lived on
 * a different domain and iPhone Safari dropped the return state. The Hub now
 * proxies that helper on its own origin, so phones can safely use redirect and
 * avoid the separate-window behavior of embedded iPhone browsers.
 *
 * Popup avoids that return trip entirely - the result comes back by
 * postMessage, not through a cross-domain cookie - which is exactly why the
 * desktop path kept working.
 *
 * The fallback is what makes this safe to flip. A popup that is BLOCKED or
 * unsupported (an in-app browser, a locked-down device) falls through to the
 * old redirect, so nobody ends up worse off than before. A popup the person
 * closed themselves is left alone: bouncing them into a full-page redirect
 * after they deliberately cancelled would be its own bug.
 *
 * Must stay callable straight from the click handler with no await in front of
 * it, or iOS treats the popup as untrusted and blocks it.
 */
async function signInPreferringPopup(
  a: Auth,
  provider: GoogleAuthProvider
): Promise<User | null> {
  if (shouldUseRedirectSignIn()) {
    markRedirectStarted();
    await signInWithRedirect(a, provider, browserPopupRedirectResolver);
    return null;
  }
  try {
    const result = await signInWithPopup(a, provider, browserPopupRedirectResolver);
    return result.user;
  } catch (err) {
    if (!shouldFallBackToRedirect(err)) {
      // A popup that opened but lost its Firebase state will not be repaired
      // by immediately starting another auth flow.
      throw normalizeOAuthSignInError(err);
    }
    markRedirectStarted();
    await signInWithRedirect(a, provider, browserPopupRedirectResolver);
    return null;
  }
}

/**
 * Kick off Google sign-in. Phones use the same-origin redirect helper;
 * desktops open a popup and fall back only if the popup cannot run.
 *
 * Returns null on mobile (redirect path) so the caller doesn't try to
 * use a User that doesn't exist yet.
 */
export async function signInWithGoogle(): Promise<User | null> {
  return signInPreferringPopup(requireAuth(), googleProvider);
}

/**
 * Pick up a pending redirect sign-in result. Called once on app mount.
 * If there's no pending redirect, this resolves to null without error.
 * Any auth error from the redirect attempt is surfaced to the caller so
 * the UI can show a friendly message.
 */
let pendingRedirectResult: Promise<User | null> | undefined;

export function consumePendingRedirect(): Promise<User | null> {
  // React StrictMode/remounts must share one consumption of Firebase's pending
  // redirect state. A real redirect reloads this module for the next attempt.
  return pendingRedirectResult ??= readPendingRedirect();
}

/**
 * False only when THIS browser positively shows no Google return is in
 * flight, read from the Hub's own redirect marker plus, when that marker
 * is gone, Firebase's own. True covers both "a return really is in flight"
 * and "cannot tell" (storage blocked, mark stale, or a reload consumed our
 * own marker on an earlier load this round trip).
 *
 * It exists for the boot deadline in lib/auth-context.tsx, which subscribes
 * to Firebase when the restore runs long. That is safe on an ordinary
 * opening and is NOT safe in the middle of a Google return: Firebase would
 * hand back the PREVIOUS account first and the person would watch the app
 * open as someone else before the return landed. So the deadline asks this
 * first, and when a return really is in flight, OR when it cannot be ruled
 * out, it shows the honest "took too long" screen instead of publishing
 * anybody. Failing closed like this only costs an honest screen instead of
 * a restored user at ten seconds; it never signs anyone out.
 *
 * The mark itself only ever decides how patient the boot is, never whether
 * a sign-in can complete: readPendingRedirect keeps Firebase's own pending
 * state as the authority for the return itself, mark or no mark.
 */
let redirectReturnState: RedirectMarkState = 'none';

export function isRedirectReturnInFlight(): boolean {
  return redirectReturnState !== 'none';
}

async function readPendingRedirect(): Promise<User | null> {
  if (!auth) return null;
  const mark = takeRecentRedirectMark();
  redirectReturnState = mark;
  // Only a mark we could actually READ and that said "fresh" is evidence
  // THIS browser started a redirect and lost the result. 'unknown' (blocked
  // or stale storage, or the fallback) must not throw that message over an
  // ordinary boot; it only feeds the in-flight guard above.
  const attempted = mark === 'inflight';
  let result: UserCredential | null;
  try {
    // Always let Firebase check its own pending marker, even if this browser
    // lost our diagnostic marker. Without a pending redirect this stays local
    // and does not initialize the OAuth iframe.
    result = await getRedirectResult(auth, browserPopupRedirectResolver);
  } catch (err) {
    throw normalizeOAuthSignInError(err);
  } finally {
    redirectReturnState = 'none';
  }
  if (result?.user) return result.user;
  // Already signed in by the time this ran (onAuthStateChanged can win the
  // race) - not a failure.
  if (auth.currentUser) return auth.currentUser;
  if (attempted) {
    // Came back from Google with nothing. Almost always the cross-origin
    // cookie drop described on PENDING_REDIRECT_KEY.
    throw new Error(REDIRECT_SIGN_IN_LOST);
  }
  return null;
}

/**
 * Create a new account with email + password. Immediately fires the
 * verification email so the user gets it while they're still on the page.
 * On the next session/page load, AuthProvider will see them as signed in
 * but with emailVerified=false, so the UI parks them on the "check your
 * email" card until they click the link.
 */
export async function signUpWithEmail(
  email: string,
  password: string
): Promise<User> {
  const a = requireAuth();
  const cred: UserCredential = await createUserWithEmailAndPassword(
    a,
    email,
    password
  );
  // Best-effort. If the verification email send fails (rare — usually
  // network), the resend button on the verification card lets the user
  // try again. Don't block sign-up on it.
  try {
    await sendEmailVerification(cred.user);
  } catch {
    /* surfaced via resendVerificationEmail */
  }
  return cred.user;
}

export async function signInWithEmail(
  email: string,
  password: string
): Promise<User> {
  const a = requireAuth();
  const cred: UserCredential = await signInWithEmailAndPassword(
    a,
    email,
    password
  );
  return cred.user;
}

/**
 * Re-send the verification email to the currently signed-in user. Used by
 * the "Didn't get it?" link on the verification card.
 */
export async function resendVerificationEmail(): Promise<void> {
  const a = requireAuth();
  if (!a.currentUser) {
    throw new Error('No signed-in user to verify.');
  }
  await sendEmailVerification(a.currentUser);
}

/**
 * Re-fetch the current user from Firebase to pick up an emailVerified flip
 * after the user clicks the link in their email. onAuthStateChanged does
 * NOT fire on verification status change; we have to reload() to see it.
 */
export async function refreshCurrentUser(): Promise<User | null> {
  const a = requireAuth();
  if (!a.currentUser) return null;
  await a.currentUser.reload();
  return a.currentUser;
}

/**
 * Send a password-reset email. Works for any account that has an
 * email/password credential. For a Google-only account Firebase will
 * still report success (it doesn't leak whether a password exists),
 * but the email won't actually let them set one — that path is
 * "Add a password" below, from inside a signed-in session.
 */
export async function sendResetEmail(email: string): Promise<void> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 12_000);
  let response: Response;
  try {
    response = await fetch('/.netlify/functions/passwordReset', {
      method: 'POST',
      headers: await publicFunctionHeaders(),
      body: JSON.stringify({ email }),
      signal: controller.signal,
    });
  } catch {
    throw new Error('We could not reach the password-reset service. Check your connection and try again.');
  } finally {
    window.clearTimeout(timer);
  }
  if (response.ok) return;
  if (response.status === 429) {
    throw new Error('Too many reset requests were made. Wait a few minutes and try again.');
  }
  throw new Error('Password reset is unavailable right now. Refresh the Hub and try again.');
}

/** The sign-in provider ids linked to the current user (e.g.
 * 'google.com', 'password'). Empty if signed out. */
export function currentProviderIds(): string[] {
  if (!auth?.currentUser) return [];
  return auth.currentUser.providerData.map((p) => p.providerId);
}

export function hasPasswordProvider(): boolean {
  return currentProviderIds().includes('password');
}

/**
 * Add an email/password credential to the CURRENTLY signed-in account
 * (e.g. someone who only ever used Google sign-in). Links the password
 * to their existing email so afterwards they can sign in either way.
 *
 * Uses the account's own verified email — we never let them attach a
 * password to a different address than the one they're signed in as.
 */
export async function addPasswordToAccount(password: string): Promise<void> {
  const a = requireAuth();
  const user = a.currentUser;
  if (!user) throw new Error('Sign in first.');
  if (!user.email) {
    throw new Error('Your account has no email to attach a password to.');
  }
  const credential = EmailAuthProvider.credential(user.email, password);
  await linkWithCredential(user, credential);
}

/**
 * Change the password on an account that already has one. May throw
 * `auth/requires-recent-login` if the session is stale — the caller
 * surfaces a "sign in again" message in that case.
 */
export async function changePassword(newPassword: string): Promise<void> {
  const a = requireAuth();
  const user = a.currentUser;
  if (!user) throw new Error('Sign in first.');
  await updatePassword(user, newPassword);
}

export async function signOut(): Promise<void> {
  if (!auth) return;
  // Captured before fbSignOut empties currentUser: the composer draft sweep
  // below is keyed by this uid.
  const signedOutUid = auth.currentUser?.uid ?? null;
  // This must happen before the first await. Kim's Safari replay proved the
  // old flow exposed a usable login form during the terminate-and-wipe
  // window. Keep every auth control blocked until the fresh-page reload.
  window.dispatchEvent(new Event(SIGNING_OUT_EVENT));
  // Detach this device's push token FIRST (while still authenticated) so the
  // next user on a shared device does not inherit this user's notifications.
  try {
    await removeCurrentDeviceToken();
    await fbSignOut(auth);
    // Belt and braces: useUserDoc()'s own effect (lib/users.ts) already
    // clears Sentry's user context reactively once `user` goes null, but
    // that is one React render away. This is the deliberate, synchronous
    // "we are signed out now" moment, and window.location.reload() below
    // means it mostly guards the narrow window between here and that
    // reload rather than doing load-bearing work on its own - cheap enough
    // to keep anyway so nothing in that window can misattribute a report to
    // the person who just signed out.
    clearSentryUser();
    // Same belt-and-braces reasoning, for the "view as" role preview
    // (packet J3 follow-up, PR #1089 review): the stored preview is already
    // scoped to the uid that started it (lib/previewRole.ts), so a
    // different account picking up this device could never inherit it even
    // without this call. This clears it eagerly anyway, wrapped exactly
    // like every other localStorage access in that module, so a device that
    // signs back in as the SAME Director does not reopen yesterday's
    // preview either.
    try {
      setPreviewRole(null);
    } catch {
      // Never block sign-out on this; the uid scope above is the real guard.
    }
    // PACKET J12 ITEM 3: forget the remembered home shell (kind plus first
    // name, lib/lastHomeShell.ts). Without this the next person to open the
    // Hub on a shared phone would see the last person's first name on the
    // boot screen for a moment before the sign-in page replaced it. The
    // shell opens no door either way; this is about whose name is on screen.
    clearLastHomeShell();
    // Every message box's kept words for this sign-in (lib/composerDrafts.ts,
    // 2026-09-15): a draft must never outlive the account on a shared
    // device. Wrapped like the sweeps above; never block sign-out on it.
    try {
      if (signedOutUid) clearComposerDraftsFor(signedOutUid);
    } catch {
      // The uid on every key is the real guard.
    }
    // PACKET J8 ROUND 2: forget the Jr Staff class payload cache (offering
    // ids by dancer id, lib/familyStaffRoles.ts). Belt and braces alongside
    // that cache's own uid scope, same reasoning as setPreviewRole(null)
    // just above - the next account on a shared device must never render
    // even one tick of a stranger's family's cached answer.
    try {
      resetFamilyStaffRolesCache();
    } catch {
      // Never block sign-out on this either; the uid scope is the real guard.
    }
    // The shared attendance concern scan (lib/attendanceConcernsClient.ts),
    // for exactly the same reason and added by the same reasoning: it is now
    // keyed by uid, and this is the belt beside that brace. Today's sign-out
    // ends in a reload, which would clear it anyway; a cache that depends on
    // a reload it does not control is a bug waiting for the first flow that
    // skips one.
    try {
      clearAttendanceConcernsCache();
    } catch {
      // Never block sign-out on this either; the uid key is the real guard.
    }
  } catch (error) {
    // Authentication did not reach the safe signed-out state, so restore the
    // current screen instead of trapping the person behind the blocker.
    window.dispatchEvent(new Event(SIGNING_OUT_CANCELLED_EVENT));
    cancelCurrentDeviceTokenRemoval();
    window.alert('We could not safely disconnect notifications from this device, so you are still signed in. Check your connection and try again.');
    throw error;
  }
  // Firestore's persistent cache is per ORIGIN, not per user: without this,
  // everything the signed-out person ever loaded (dancer DOBs, parent
  // contacts, injury notes, payroll) stays readable in IndexedDB on a shared
  // device. terminate() must come first - clearing refuses on a live
  // instance. The page reload after gives the app a fresh Firestore handle,
  // since the terminated one cannot be reused; sign-out already lands on the
  // signed-out home, so the reload is invisible in the flow.
  if (db) {
    // Stamp the shared reload cooldown BEFORE the wipe, not just before the
    // reload: the freshness auto-update acts on foreground and navigation
    // events, and one landing between terminate() and the reload below could
    // restart the page mid-wipe on the shared iPad this exists to protect.
    // Stamping first makes the whole terminate-wipe-reload sequence sit
    // inside the cooldown window where automatic reloads refuse to act.
    markReload();
    try {
      await terminate(db);
      await clearIndexedDbPersistence(db);
    } catch {
      // Another open tab can hold the cache lease; the wipe then fails, but
      // sign-out itself must not. That tab's user is still signed out.
    }
  }
  // Always replace the terminated singleton, even if Firestore was not
  // configured or the cache wipe was unavailable. The blocking screen stays
  // mounted until this reload, so another sign-in cannot race the cleanup.
  window.location.reload();
}
